import { LocalizableError } from '@sparelabs/error-types'
import pino, {
  Bindings,
  LoggerOptions,
  Logger as PinoLogger,
  SerializedError,
  SerializerFn,
  TransportMultiOptions,
  TransportPipelineOptions,
  TransportSingleOptions,
} from 'pino'
import { LogLevel } from './LogTypes'
import { machineReadableLogs } from './machineReadableLogs'

const SILENT_LOG_LEVEL: string = 'silent'

const messageKey: string = 'message'

// Map pino log levels to GCP log levels
const gcpLogLevels: Record<string, string> = {
  [LogLevel.Error]: 'ERROR',
  [LogLevel.Warn]: 'WARNING',
  [LogLevel.Info]: 'INFO',
  [LogLevel.Trace]: 'DEBUG',
}

interface IPinoFormatters {
  level?: (label: string, number: number) => object
  bindings?: (bindings: Bindings) => object
  log?: (object: Record<string, unknown>) => Record<string, unknown>
}

type IPinoTransports = TransportSingleOptions | TransportMultiOptions | TransportPipelineOptions

export class PinoLoggerBuilder {
  public static build(name?: string): PinoLogger {
    const machineReadable = machineReadableLogs()
    const level = this.buildLevel()
    const isSilent = level === SILENT_LOG_LEVEL
    const pinoOptions: LoggerOptions = {
      ...(name && { name }),
      level: this.buildLevel(),
      messageKey,
      formatters: isSilent ? undefined : this.buildFormatters(machineReadable),
      transport: isSilent ? undefined : this.buildTransport(machineReadable),
      serializers: isSilent ? undefined : this.buildSerializers(machineReadable),
    }
    // In Jest tests, the logs sometimes gets lost, this workaround fixes that
    return this.isJest() ? pino(pinoOptions, pino.destination()) : pino(pinoOptions)
  }

  private static isTest(): boolean {
    return process.env.NODE_ENV?.toLowerCase() === 'test'
  }

  private static isJest(): boolean {
    return Boolean(process.env.JEST_WORKER_ID)
  }

  private static buildLevel(): string {
    const defaultLevel = this.isTest() ? SILENT_LOG_LEVEL : LogLevel.Info
    return process.env.LOG_LEVEL?.toLowerCase() ?? defaultLevel
  }

  private static buildFormatters(machineReadable: boolean): IPinoFormatters | undefined {
    if (machineReadable) {
      return {
        level: (label: string) => ({ severity: gcpLogLevels[label] ?? 'ERROR' }),
      }
    }
    return undefined
  }

  private static buildTransport(machineReadable: boolean): IPinoTransports | undefined {
    if (machineReadable) {
      return undefined
    }
    return {
      target: 'pino-pretty',
      options: {
        colorize: true,
        singleLine: true,
        messageKey,
        translateTime: 'SYS:yyyy-mm-dd HH:MM:ss',
        ignore: 'pid,hostname',
      },
    }
  }

  private static buildSerializers(machineReadable: boolean): { [key: string]: SerializerFn } | undefined {
    if (machineReadable) {
      return undefined
    }

    const errorSerializer = (error: Error): SerializedError => {
      try {
        let message = error.message
        const localizableError = error as Partial<LocalizableError>
        if (!message && localizableError.messageInput) {
          if (typeof localizableError.messageInput === 'string') {
            message = localizableError.messageInput
          } else if (typeof localizableError.messageInput === 'function') {
            message = localizableError.messageInput(localizableError.localizeOptions, 'en')
          }
        }
        const metadata = localizableError.metadata ? JSON.stringify(localizableError.metadata) : undefined
        const serializedError = pino.stdSerializers.err(error)
        serializedError.message = message
        serializedError.metadata = metadata
        return serializedError
      } catch (_error) {
        return pino.stdSerializers.err(error)
      }
    }

    return { error: errorSerializer }
  }
}
